import React, { useState, useEffect, useCallback } from 'react';
import { FaCopy } from 'react-icons/fa';
import { gapi } from 'gapi-script';
import 'bootstrap/dist/css/bootstrap.min.css';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
const SCOPES = 'https://www.googleapis.com/auth/calendar.events';

const GenerateMeetLink = () => {
  const [meetLink, setMeetLink] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedTime, setSelectedTime] = useState('12:00');

  // Memoize handleClientLoad to prevent unnecessary re-renders
  const handleClientLoad = useCallback(() => {
    gapi.load('client:auth2', initClient);
  }, []);

  // Load the Google API client when component mounts
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = handleClientLoad;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [handleClientLoad]);

  const initClient = () => {
    gapi.client
      .init({
        apiKey: API_KEY,
        clientId: CLIENT_ID,
        discoveryDocs: [
          'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest',
        ],
        scope: SCOPES,
      })
      .then(() => {
        // Handle initial sign-in state
        if (!gapi.auth2.getAuthInstance().isSignedIn.get()) {
          gapi.auth2.getAuthInstance().signIn();
        }
      })
      .catch((error) => {
        console.error('Error initializing Google API client:', error);
        alert('Failed to initialize Google API. Please try again.');
      });
  };

  const createEvent = () => {
    setIsLoading(true);

    // Combine selected date with selected time
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const eventDate = new Date(selectedDate);
    eventDate.setHours(hours, minutes, 0, 0);

    const event = {
      summary: 'Google Meet Event',
      description: 'Generated Google Meet link using Google Calendar API',
      start: {
        dateTime: eventDate.toISOString(),
        timeZone: 'Europe/Berlin',
      },
      end: {
        dateTime: new Date(eventDate.getTime() + 30 * 60 * 1000).toISOString(),
        timeZone: 'Europe/Berlin',
      },
      conferenceData: {
        createRequest: {
          requestId: Math.random().toString(36).substring(2, 15),
          conferenceSolutionKey: { type: 'hangoutsMeet' },
        },
      },
    };

    gapi.client.calendar.events
      .insert({
        calendarId: 'primary',
        resource: event,
        conferenceDataVersion: 1,
      })
      .then((response) => {
        setMeetLink(response.result.hangoutLink);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error creating event:', error);
        setIsLoading(false);
        alert('Failed to create meeting. Please try again.');
      });
  };

  const handleGenerateLink = () => {
    if (gapi.auth2 && gapi.auth2.getAuthInstance()?.isSignedIn.get()) {
      createEvent();
    } else {
      gapi.auth2
        ?.getAuthInstance()
        ?.signIn()
        .then(createEvent)
        .catch((error) => {
          console.error('Error signing in:', error);
          alert('Failed to sign in. Please try again.');
        });
    }
  };

  const copyToClipboard = () => {
    if (meetLink) {
      navigator.clipboard.writeText(meetLink);
      alert('Link copied to clipboard!');
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowTimePicker(true);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        times.push(
          <option key={timeString} value={timeString}>
            {timeString}
          </option>
        );
      }
    }
    return times;
  };

  return (
    <div className='container mt-5'>
      <div
        className='card shadow-sm p-4'
        style={{ maxWidth: '600px', margin: '0 auto' }}
      >
        <h2 className='mb-4 text-center'>Generate Google Meet Link</h2>
        <div className='mb-3'>
          <label className='form-label'>Select Date</label>
          <Calendar
            onChange={handleDateChange}
            value={selectedDate}
            minDate={new Date()}
            className='meet-calendar'
          />
        </div>

        {showTimePicker && (
          <div className='mb-3'>
            <label className='form-label'>Select Time</label>
            <select
              className='form-select'
              value={selectedTime}
              onChange={handleTimeChange}
            >
              {generateTimeOptions()}
            </select>
          </div>
        )}

        <button
          onClick={handleGenerateLink}
          className='btn btn-primary mb-3'
          disabled={isLoading || !API_KEY || !CLIENT_ID || !showTimePicker}
          style={{ width: '100%' }}
        >
          {isLoading ? 'Generating...' : 'Generate Link'}
        </button>

        {meetLink && (
          <div className='input-group mb-3'>
            <input
              type='text'
              className='form-control'
              value={meetLink}
              readOnly
            />
            <button
              onClick={copyToClipboard}
              className='btn btn-outline-secondary'
              type='button'
            >
              <FaCopy className='me-2' />
              Copy
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateMeetLink;
